// ============================================================================
// LandingCta — the closing call-to-action band. A bold coral→orange gradient
// panel (built from the locked Pulse gradient + glow tokens) with a single,
// unmistakable login entry. Personalised for a returning trainer. Reveals on
// scroll; a soft animated sheen sweeps across on larger screens.
// ============================================================================
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "../../components";
import { CLIENT_LOGIN_PATH } from "../../client/routes";
import type { ReturningTrainer } from "./useReturningTrainer";

const EASE = [0.22, 0.61, 0.36, 1] as const;

function ArrowRight() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path
        d="M5 12h14m-6-6 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LandingCta({ session }: { session: ReturningTrainer }) {
  const { t } = useTranslation("landing");
  const reduce = useReducedMotion();

  // New visitors are pushed to create an account; returning trainers resume.
  const href = session.isReturning ? "/app" : "/signup";
  const label = session.isReturning ? t("hero.resume") : t("cta.signUp");

  return (
    <section className="bg-paper py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-shell px-gutter">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="relative isolate overflow-hidden rounded-2xl bg-grad-hero px-6 py-14 text-center shadow-volt-lg sm:px-12 sm:py-16"
        >
          {/* Hero glow + a sweeping sheen for life. */}
          <div aria-hidden className="pointer-events-none absolute inset-0 bg-grad-hero-glow" />
          {!reduce && (
            <motion.div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-white/20 blur-2xl"
              animate={{ x: ["0%", "420%"] }}
              transition={{ duration: 4.5, repeat: Infinity, repeatDelay: 2.5, ease: EASE }}
            />
          )}

          <p className="font-display text-sm font-bold uppercase tracking-kicker text-white/85">
            {t("cta.kicker")}
          </p>
          <h2 className="mx-auto mt-3 max-w-2xl font-display font-extrabold tracking-tightest text-white text-[clamp(2rem,1.3rem+3.4vw,3.4rem)]">
            {t("cta.title")}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/90 sm:text-lg">
            {t("cta.subtitle")}
          </p>

          <div className="mt-8 flex justify-center">
            <Link to={href}>
              {/* Ghost variant reads as a clean white pill on the gradient. */}
              <Button size="lg" variant="ghost" iconRight={<ArrowRight />}>
                {label}
              </Button>
            </Link>
          </div>

          {/* TWO clear login doorways for existing accounts — only for a brand-
              new visitor (a returning trainer's CTA already resumes the studio). */}
          {!session.isReturning && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 text-sm">
              <Link
                to="/login"
                className="font-bold text-white underline-offset-4 transition-opacity hover:underline hover:opacity-90"
              >
                {t("cta.trainerLogin")}
              </Link>
              <span className="text-white/60" aria-hidden>
                ·
              </span>
              <Link
                to={CLIENT_LOGIN_PATH}
                className="font-bold text-white underline-offset-4 transition-opacity hover:underline hover:opacity-90"
              >
                {t("cta.clientLogin")}
              </Link>
            </div>
          )}

          <p className="mt-6 font-display text-sm font-bold text-white/80">
            {t("cta.note")}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
